import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { getEmployeesAttendance } from "../../../core/data/redux/employeesAttendanceSlice";
import { all_routes } from "../../router/all_routes";
import MonthYearDropdown from "./MonthYearDropdown";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { formatDateTime } from "../../../utils/dateUtils";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AttendanceFilter, { AttendanceFilterValues } from "../../../core/common/AttendanceFilter";
// Helper function to check if search matches Emp ID (handles both formatted and original)
const matchesEmpId = (empId: any, searchValue: string): boolean => {
  if (!empId || !searchValue || !searchValue.trim()) return false;

  const empIdStr = empId.toString().toLowerCase().trim();
  const formattedEmpId = formatEmpIdForDisplay(empId).toLowerCase().trim();
  const searchStr = searchValue.toLowerCase().trim();

  // Check both original and formatted Emp ID
  return empIdStr.includes(searchStr) || formattedEmpId.includes(searchStr);
};


const AttendanceSummary = () => {
  const routes = all_routes;
  const dispatch = useDispatch();
  const attendanceState = useSelector(
    (state: any) => state.employeesAttendanceState
  );

  const [dataList, setDataList] = useState<any[]>([]);
  const [originalDataList, setOriginalDataList] = useState<any[]>([]); // Store original unfiltered data
  const [query, setQuery] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [month, setMonth] = useState<string>(
    (new Date().getMonth() + 1).toString()
  );
  const [year, setYear] = useState<string>(new Date().getFullYear().toString());

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });

  // Filter state
  const [filterValues, setFilterValues] = useState<AttendanceFilterValues>({
    wing: "",
    department: "",
    office_location_id: "",
    date_from: "",
    date_to: "",
  });

  const fetchRecords = (pagination: any, query: string) => {
    dispatch(
      getEmployeesAttendance({
        page: pagination.current,
        limit: pagination.pageSize,
        month: month,
        year: year,
        query,
      })
    );
  };


  // Frontend filtering function
  const applyFrontendFilters = (data: any[], filters: AttendanceFilterValues) => {
    let filteredData = [...data];

    // Filter by Wing
    if (filters.wing) {
      filteredData = filteredData.filter((item) => {
        const wing =
          item?.employee?.details?.sap_department_designation_wings?.wing;
        return wing === filters.wing;
      });
    }

    // Filter by Department
    if (filters.department) {
      filteredData = filteredData.filter((item) => {
        const deptId =
          item?.employee?.details?.sap_department_designation_wings
            ?.department_id || item?.employee?.details?.department;
        return deptId === filters.department;
      });
    }

    // Filter by Assigned Location (office_location_id)
    if (filters.office_location_id) {
      filteredData = filteredData.filter((item) => {
        const locId = item?.office_location_id || item?.officeLocation?.id;
        return locId && locId.toString() === filters.office_location_id;
      });
    }

    // Filter by Date Range (From Date)
    if (filters.date_from) {
      const fromDate = new Date(filters.date_from);
      fromDate.setHours(0, 0, 0, 0);
      filteredData = filteredData.filter((item) => {
        const checkInDate = item?.check_in_time
          ? new Date(item.check_in_time)
          : null;
        if (!checkInDate) return false;
        checkInDate.setHours(0, 0, 0, 0);
        return checkInDate >= fromDate;
      });
    }

    // Filter by Date Range (To Date)
    if (filters.date_to) {
      const toDate = new Date(filters.date_to);
      toDate.setHours(23, 59, 59, 999);
      filteredData = filteredData.filter((item) => {
        const checkInDate = item?.check_in_time
          ? new Date(item.check_in_time)
          : null;
        if (!checkInDate) return false;
        return checkInDate <= toDate;
      });
    }

    return filteredData;
  };

  // Handle filter change
  const handleFilterChange = (filters: AttendanceFilterValues) => {
    setFilterValues(filters);
  };

  // Handle search button click - Apply frontend filters
  const handleFilterSearch = () => {
    let filteredData = applyFrontendFilters(originalDataList, filterValues);

    // Apply Emp ID search filter
    if (searchQuery) {
      filteredData = filteredData.filter((item) => {
        const empId = item?.empId || item?.employee?.empId || "";
        return empId.toString().toLowerCase().includes(searchQuery.toLowerCase());
      });
    }

    setDataList(filteredData);
    setPagination({
      ...pagination,
      current: 1,
      total: filteredData.length,
    });
  };

  // Handle reset
  const handleFilterReset = () => {
    const resetFilters: AttendanceFilterValues = {
      wing: "",
      department: "",
      office_location_id: "",
      date_from: "",
      date_to: "",
    };
    setFilterValues(resetFilters);
    setSearchQuery("");
    setDataList(originalDataList);
    setPagination({
      ...pagination,
      current: 1,
      total: originalDataList.length,
    });
  };

  // Download CSV function
  const handleDownloadCSV = () => {
    try {
      const data = dataList;

      if (data.length === 0) {
        toast.warning("No data available to export");
        return;
      }

      const csvHeaders = [
        "Sr. No.",
        "Emp ID",
        "Approver",
        "Wing",
        "Department",
        "Assigned Location",
        "Checkin Location",
        "Checkout Location",
        "Checkin Time",
        "Checkout Time",
        "Working Hours",
        "Status",
        "Work Type",
        "Is Verified",
      ];

      const csvRows = data.map((item: any, index: number) => {
        const approverEmpId = item.approverEmpId
          ? parseInt(item.approverEmpId, 10)
          : "";
        const approverName = item?.approver?.firstname
          ? `${item.approver.firstname} ${item.approver.lsastname || ""}`.trim()
          : "";
        const deptInfo =
          item?.employee?.details?.sap_department_designation_wings;

        return [
          index + 1,
          item.empId ? formatEmpIdForDisplay(item.empId) : "",
          approverName ? `${approverEmpId} - ${approverName}` : approverEmpId,
          deptInfo?.wing_name || "",
          deptInfo?.department_name || "",
          item.officeLocation?.address || "",
          `${item.check_in_latitude || ""}, ${item.check_in_longitude || ""}`,
          `${item.check_out_latitude || ""}, ${item.check_out_longitude || ""}`,
          item.check_in_time ? formatDateTime(item.check_in_time) : "",
          item.check_out_time ? formatDateTime(item.check_out_time) : "",
          item.working_hours || "",
          item.attendance_status || "",
          item.attendance_work_type || "",
          item.is_verified || "",
        ];
      });

      const csvContent = [
        csvHeaders.join(","),
        ...csvRows.map((row: any[]) =>
          row
            .map((cell: any) => `"${String(cell).replace(/"/g, '""')}"`)
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `attendance_summary_${month}_${year}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("CSV downloaded successfully");
    } catch (error: any) {
      console.error("Error downloading CSV:", error);
      toast.error("Failed to download CSV");
    }
  };

  const handlePagination = (newPagination: any) => {
    const updatedPagination = {
      current: newPagination.current || newPagination.page || 1,
      pageSize: newPagination.pageSize || 10,
      total: newPagination.total || pagination.total,
    };
    setPagination(updatedPagination);
    fetchRecords(updatedPagination, query);
  };

  const handleSearch = (searchValue: string) => {
    setSearchQuery(searchValue);
    // Apply frontend search on already loaded data
    let filteredData = applyFrontendFilters(originalDataList, filterValues);

    // Apply Emp ID search filter
    if (searchValue && searchValue.trim()) {
      filteredData = filteredData.filter((item) => {
        const empId = item?.empId || item?.employee?.empId || "";
        return matchesEmpId(empId, searchValue);
      });
    }

    setDataList(filteredData);
    setPagination({
      ...pagination,
      current: 1,
      total: filteredData.length,
    });
  };

  useEffect(() => {
    fetchRecords(pagination, query);
  }, []);

  useEffect(() => {
    // Reset to page 1 when month/year changes
    const resetPagination = { ...pagination, current: 1 };
    setPagination(resetPagination);
    fetchRecords(resetPagination, query);
  }, [month, year]);

  useEffect(() => {
    if (
      attendanceState.loading === "fulfilled" &&
      attendanceState.action === "list"
    ) {
      const result = attendanceState.data?.data || null;

      if (result) {
        const dataArray = result?.data || [];
        const total = result?.total || dataArray.length;
        const currentPage = result?.page || 1;
        const pageSize = result?.limit || pagination.pageSize;

        const processedData = Array.isArray(dataArray) ? dataArray : [];
        setOriginalDataList(processedData); // Store original data

        // Apply filters and search if they exist
        let filteredData = applyFrontendFilters(processedData, filterValues);
        if (searchQuery && searchQuery.trim()) {
          filteredData = filteredData.filter((item) => {
            const empId = item?.empId || item?.employee?.empId || "";
            return matchesEmpId(empId, searchQuery);
          });
        }

        setDataList(filteredData);
        setPagination({
          current: currentPage,
          pageSize: pageSize,
          total: filteredData.length,
        });
        // Reset filters when new data is loaded
        setFilterValues({
          wing: "",
          department: "",
          office_location_id: "",
          date_from: "",
          date_to: "",
        });
        setSearchQuery("");
      } else {
        setDataList([]);
        setOriginalDataList([]);
        setPagination((prev) => ({
          ...prev,
          total: 0,
        }));
      }
    }
  }, [attendanceState.loading]);

  const columns = [
    {
      title: "Sr. No.",
      render: (_: any, __: any, index: number) =>
        index + 1 + (pagination.current - 1) * pagination.pageSize,
    },
    {
      title: "Emp ID",
      dataIndex: "empId",
      render: (empId: string) => <span>{formatEmpIdForDisplay(empId)}</span>,
    },
    {
      title: "Approver",
      dataIndex: "approverEmpId",
      render: (empId: string, record: any) => {
        if (!empId) return "-";

        const approverEmpId = parseInt(empId, 10);
        // Try to get approver name from the record if available
        const approverName = record?.approver?.firstname
          ? `${record.approver.firstname} ${record.approver.lsastname || ""
            }`.trim()
          : record?.approverName || "";

        if (approverName) {
          return (
            <span>
              {approverEmpId} - {approverName}
            </span>
          );
        }
        return <span>{approverEmpId}</span>;
      },
    },
    {
      title: "Wing",
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.wing_name || "-",
    },
    {
      title: "Department",
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.department_name ||
        "-",
    },
    {
      title: "Assigned Location",
      dataIndex: "officeLocation",
      render: (officeLocation: any) => officeLocation?.address || "-",
    },
    {
      title: "Checkin Location",
      dataIndex: "check_in_latitude",
      render: (text: any, record: any) => (
        <span>
          {text}, {record.check_in_longitude}
        </span>
      ),
    },
    {
      title: "Checkout Location",
      dataIndex: "check_out_latitude",
      render: (text: any, record: any) => (
        <span>
          {text}, {record.check_out_longitude}
        </span>
      ),
    },
    {
      title: "Checkin Time",
      dataIndex: "check_in_time",
      render: (text: any) => formatDateTime(text) || "",
    },
    {
      title: "Checkout Time",
      dataIndex: "check_out_time",
      render: (text: any) => formatDateTime(text) || "",
    },
    {
      title: "Working Hours",
      dataIndex: "working_hours",
    },
    {
      title: "Status",
      dataIndex: "attendance_status",
      render: (text: any) => <span>{text}</span>,
    },
    {
      title: "Work Type",
      dataIndex: "attendance_work_type",
      render: (text: any) => <span>{text}</span>,
    },
    {
      title: "Is verified?",
      dataIndex: "is_verified",
      render: (text: any) => <span>{text}</span>,
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Attendance Summary</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Attendance</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Summary
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap"></div>
        </div>
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-4 mt-3">Attendance Summary</h4>
            <MonthYearDropdown
              onChange={({ month, year }) => {
                setMonth(month);
                setYear(year);
              }}
              month={""}
              year={year}
            />
          </div>
          {/* Filter Section */}
          <div className="px-3 pt-3">
            <AttendanceFilter
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onSearch={handleFilterSearch}
              onReset={handleFilterReset}
              onDownloadCSV={handleDownloadCSV}
              showDownload={true}
              attendanceData={originalDataList}
            />
          </div>
          {/* <pre>{JSON.stringify(dataList, null, 2)}</pre> */}
          <div className="card-body p-0 py-3">
            <Table
              columns={columns}
              dataSource={dataList}
              Selection={false}
              paginationConfig={pagination}
              handlePagination={handlePagination}
              loading={attendanceState.loading === "pending"}
              handleSearch={handleSearch}
              searchPlaceholder="Search by Emp ID"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceSummary;
